import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  Avatar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
} from "@mui/material";
import {
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material";
import { red, green, amber, blue, grey } from "@mui/material/colors";

const PaymentStatusModal = ({ open, payment, onClose, onConfirm, loading }) => {
  const [statusData, setStatusData] = useState({
    status: "",
    notes: "",
  });
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (open && payment) {
      setStatusData({
        status: payment.status || "",
        notes: "",
      });
      setFormError("");
    }
  }, [open, payment]);

  const handleStatusChange = (e) => {
    setStatusData((prev) => ({
      ...prev,
      status: e.target.value,
    }));
    if (formError) {
      setFormError("");
    }
  };

  const handleNotesChange = (e) => {
    setStatusData((prev) => ({
      ...prev,
      notes: e.target.value,
    }));
    if (formError) {
      setFormError("");
    }
  };

  const handleConfirm = () => {
    // Validate status change
    if (!statusData.status) {
      setFormError("Please select a status");
      return;
    }

    if (statusData.status === payment?.status) {
      setFormError("Please select a different status");
      return;
    }

    // Validate notes for certain status changes
    if (
      (statusData.status === "failed" || statusData.status === "canceled") &&
      !statusData.notes.trim()
    ) {
      setFormError("Please provide a reason for this status change");
      return;
    }

    if (statusData.notes.trim().length > 500) {
      setFormError("Notes cannot exceed 500 characters");
      return;
    }

    onConfirm({
      status: statusData.status,
      notes: statusData.notes.trim() || undefined,
    });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "completed":
        return {
          icon: <CheckCircleIcon sx={{ color: green[500] }} />,
          color: green[500],
          label: "Completed",
          description: "Mark this payment as successfully completed",
        };
      case "failed":
        return {
          icon: <ErrorIcon sx={{ color: red[500] }} />,
          color: red[500],
          label: "Failed",
          description: "Mark this payment as failed",
        };
      case "pending":
        return {
          icon: <WarningIcon sx={{ color: amber[500] }} />,
          color: amber[500],
          label: "Pending",
          description: "Mark this payment as pending processing",
        };
      case "canceled":
        return {
          icon: <CancelIcon sx={{ color: grey[500] }} />,
          color: grey[500],
          label: "Canceled",
          description: "Mark this payment as canceled",
        };
      default:
        return {
          icon: <EditIcon sx={{ color: blue[500] }} />,
          color: blue[500],
          label: "Unknown",
          description: "Update payment status",
        };
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getAvailableStatuses = () => {
    if (!payment) return [];

    const currentStatus = payment.status;
    const allStatuses = [
      { value: "pending", label: "Pending" },
      { value: "completed", label: "Completed" },
      { value: "failed", label: "Failed" },
      { value: "canceled", label: "Canceled" },
    ];

    // Filter out current status and refunded (refunded is handled separately)
    return allStatuses.filter(
      (status) => status.value !== currentStatus && status.value !== "refunded"
    );
  };

  const isStatusChangeRestricted = () => {
    if (!payment) return false;
    return payment.status === "refunded";
  };

  const currentStatusConfig = payment ? getStatusConfig(payment.status) : null;
  const newStatusConfig = getStatusConfig(statusData.status);

  if (!payment) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: blue[500],
              width: 40,
              height: 40,
            }}
          >
            <EditIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Update Payment Status
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Payment ID: {payment.id?.slice(-12)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {/* Payment Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Payment Summary
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: grey[50],
              borderRadius: 2,
              border: `1px solid ${grey[200]}`,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(payment.amount, payment.currency)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  User
                </Typography>
                <Typography variant="body2">
                  {payment.username || payment.user_email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body2">
                  {payment.payment_type?.replace("_", " ").toUpperCase()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Current Status
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  {currentStatusConfig?.icon}
                  <Chip
                    label={
                      currentStatusConfig?.label ||
                      payment.status?.toUpperCase()
                    }
                    size="small"
                    sx={{
                      bgcolor: currentStatusConfig?.color,
                      color: "white",
                      fontWeight: 500,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Status Change Restriction Warning */}
        {isStatusChangeRestricted() && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Status Change Restricted:</strong> Refunded payments
              cannot have their status changed.
            </Typography>
          </Alert>
        )}

        {/* Status Selection */}
        {!isStatusChangeRestricted() && (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="status-select-label">New Status</InputLabel>
              <Select
                labelId="status-select-label"
                label="New Status"
                value={statusData.status}
                onChange={handleStatusChange}
                disabled={loading}
              >
                {getAvailableStatuses().map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {getStatusConfig(status.value).icon}
                      {status.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status Change Preview */}
            {statusData.status && statusData.status !== payment.status && (
              <Alert
                severity={
                  statusData.status === "completed"
                    ? "success"
                    : statusData.status === "failed"
                    ? "error"
                    : statusData.status === "canceled"
                    ? "warning"
                    : "info"
                }
                sx={{ mb: 3 }}
              >
                <Typography variant="body2">
                  <strong>Status Change:</strong> {newStatusConfig.description}
                </Typography>
              </Alert>
            )}

            {/* Notes Input */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label={
                statusData.status === "failed" ||
                statusData.status === "canceled"
                  ? "Reason for status change *"
                  : "Additional notes (optional)"
              }
              placeholder={
                statusData.status === "failed"
                  ? "e.g., Payment processing error, insufficient funds..."
                  : statusData.status === "canceled"
                  ? "e.g., User requested cancellation, duplicate payment..."
                  : statusData.status === "completed"
                  ? "e.g., Payment verified manually, resolved payment issue..."
                  : "Any additional notes about this status change..."
              }
              value={statusData.notes}
              onChange={handleNotesChange}
              error={!!formError}
              helperText={
                formError || `${statusData.notes.length}/500 characters`
              }
              disabled={loading}
              variant="outlined"
            />

            {/* Warning for Critical Status Changes */}
            {(statusData.status === "failed" ||
              statusData.status === "canceled") && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Warning:</strong> This action will mark the payment as{" "}
                  {statusData.status}. Make sure this is correct as it may
                  affect user account balances and access.
                </Typography>
              </Alert>
            )}

            {statusData.status === "completed" &&
              payment.status === "pending" && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Marking this payment as completed
                    will credit the user's account with the purchased
                    coins/benefits.
                  </Typography>
                </Alert>
              )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        {!isStatusChangeRestricted() && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={
              loading ||
              !statusData.status ||
              statusData.status === payment.status
            }
            sx={{
              background:
                statusData.status === "completed"
                  ? "linear-gradient(45deg, #4caf50, #66bb6a)"
                  : statusData.status === "failed"
                  ? "linear-gradient(45deg, #f44336, #ef5350)"
                  : statusData.status === "canceled"
                  ? "linear-gradient(45deg, #ff9800, #ffb74d)"
                  : "linear-gradient(45deg, #1976d2, #42a5f5)",
              "&:hover": {
                background:
                  statusData.status === "completed"
                    ? "linear-gradient(45deg, #388e3c, #4caf50)"
                    : statusData.status === "failed"
                    ? "linear-gradient(45deg, #d32f2f, #f44336)"
                    : statusData.status === "canceled"
                    ? "linear-gradient(45deg, #f57c00, #ff9800)"
                    : "linear-gradient(45deg, #1565c0, #1976d2)",
              },
            }}
            startIcon={
              loading ? <CircularProgress size={16} /> : newStatusConfig.icon
            }
          >
            {loading ? "Updating..." : `Update to ${newStatusConfig.label}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentStatusModal;
